import React, { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Menu, EyeOff, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, ErrorModel, Loading, LogoutModel, SideMenu } from '../components';
import topImage from '../assets/images/homeTop.jpg';
import DeactivateModel from '../components/DeactivateModel';
import { useGetMyDetail, useSubscriptionData } from '../service';
import { useDispatch } from 'react-redux';
import premiumBadge from '../assets/images/premium-badge.png';
import { updateProfileViewStatus, updateUserDetail } from '../store/userSlice';
import { useQueryClient } from 'react-query';
import PlanExpiryModal from '../components/PlanExpiryModal';
import { hasShownToday, markShownToday } from '../utils/planExpiryStorage';

const HomeLayout: React.FC = () => {

    const navigate = useNavigate();
    const queryClient = useQueryClient()
    const dispatch = useDispatch()

    const { data, isLoading, isError } = useGetMyDetail()
    const { data: subscriptionData } = useSubscriptionData()

    const [menuOpen, setMenuOpen] = useState(false);
    const [isDeactivate, setIsDeactivate] = useState(false);
    const [isLogout, setIsLogout] = useState(false);
    const [expiryPopup, setExpiryPopup] = useState<{ daysRemaining: number; endDate: string } | null>(null);

    useEffect(() => {
        if (data?.data) {
            dispatch(
                updateUserDetail({
                    userHoroscope: data?.data.horoscope_file as any,
                    userImages: data?.data.profile_images,
                    is_premium: data?.is_premium,
                    first_name: data.data.first_name,
                    last_name: data.data.last_name,
                    gender: data.data.gender,
                    isLegitPremium: data.data.isLegitPremium,
                })
            )

            dispatch(
                updateProfileViewStatus({
                    maxProfileViewLimit: data?.data.maxProfileViewLimit,
                    profileViewCount: data?.data.profileViewCount,
                })
            )

            if (data?.data.maxProfileViewLimit === data?.data.profileViewCount) {
                queryClient.invalidateQueries(["subscriptions"])
            }
        }
    }, [data])

    useEffect(() => {
        if (!data?.is_premium) return;
        if (hasShownToday()) return;

        const currentPlan = subscriptionData?.data?.plans?.find(p => p.is_current_plan);
        if (!currentPlan?.current_plan_end_date) return;

        const endDate = new Date(currentPlan.current_plan_end_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        const daysRemaining = Math.round((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        if (daysRemaining >= 0 && daysRemaining <= 3) {
            setExpiryPopup({ daysRemaining, endDate: currentPlan.current_plan_end_date as string });
        }
    }, [subscriptionData, data?.is_premium]);

    {/* inactive user view */ }
    if (data?.data.is_active === false) {
        return (
            <>
                <div className='flex flex-col  items-center gap-5 font-outfit mt-6'>
                    <div className='flex items-center justify-center rounded-full border-4 border-textLight w-24 h-24'>
                        <EyeOff className='text-textLight size-14' />
                    </div>
                    <h1 className='text-2xl font-bold text-primary'>Account Deactivated</h1>
                    <span className='text-lg text-textColor text-center mx-6 md:mx-10 lg:mx-24 mb-2'>
                        Your account is currently inactive and inaccessible.<br></br>
                        To reactivate your account, please contact our support team at <a href='mailto:support@valluvanadmatrimonial.com' className="text-lg text-primary font-semibold hover:text-textLight underline">
                            support@valluvanadmatrimonial.com
                        </a> or call <a href='tel:+916282647660' className="text-lg text-primary font-semibold hover:text-textLight underline">
                            +91 6282647660
                        </a>.
                    </span>
                    <div className='flex gap-2'>
                        <Button name='Logout' type='cancel' htmlType='button' onClick={() => setIsLogout(true)} />
                        <Button name='Home' type='update' htmlType='button' onClick={() => navigate('/')} />
                    </div>
                </div>
                <LogoutModel
                    isOpen={isLogout}
                    onClose={() => {
                        setIsLogout(false);
                    }}
                />
            </>
        )
    }

    return (
        <>
            {isLoading ? (
                <div className='flex min-h-screen justify-center items-center pb-24'>
                    <Loading />
                </div>
            ) : isError ? (
                <ErrorModel />
            ) : (
                <div className='min-h-screen overflow-x-hidden '>
                    <div className='h-32 relative'>
                        <img src={topImage} alt="topImage" className='w-full h-full object-cover -z-10 ' />

                        <div
                            className='absolute top-4 left-4 cursor-pointer text-secondary lg:hidden bg-primary p-2 rounded-full z-20'
                            onClick={() => setMenuOpen(!menuOpen)}
                        >
                            {menuOpen ? <X className='w-8 h-8 text-secondary' /> : <Menu className='w-8 h-8' />}
                        </div>

                        <>
                            {/* old premium badge */}
                            {/* <div className="absolute right-4 top-1/2 w-fit h-24 bg-secondary rounded-lg shadow-all 
                                transform -translate-y-1/2 flex px-3 md:px-4 lg:px-6 items-center border-2 border-goldenDark ">
                                    <div className='flex flex-col gap-2'>
                                        <h1 className='text-primary text-md font-outfit font-bold text-center '>Unlock Better Matches</h1>
                                        <div className='flex w-fit mx-auto'>
                                            <Button name='Pay Now' type='first' htmlType='button' onClick={() => navigate('subscription')} />
                                        </div>
                                    </div>
                                </div> */}

                            {!data?.is_premium && (
                                <div className="absolute w-full md:w-1/2 md:rounded-r-full top-1/2 mt-8 h-fit  shadow-all 
                                transform -translate-y-1/2 flex px-3 md:px-4 lg:px-6 items-center py-2 bg-white/70 backdrop-blur-none lg:hidden xl:hidden">
                                    <div className='flex justify-between w-full'>
                                        <div className='flex gap-2'>
                                            <img src={premiumBadge} alt='premiumBadge' className='size-8' />
                                            <h1 className='text-primary text-md font-outfit font-bold text-center my-auto'>Unlock Better Matches</h1>
                                        </div>
                                        <Button name='Upgrade Now' type='first' htmlType='button' onClick={() => navigate('subscription')} />
                                    </div>
                                </div>
                            )}
                        </>
                    </div>

                    <AnimatePresence>
                        {menuOpen && (
                            <motion.div
                                initial={{ x: '-100%' }}
                                animate={{ x: 10 }}
                                exit={{ x: '-100%' }}
                                transition={{ duration: 0.3 }}
                                className='fixed top-28 left-0 bottom-24 w-64 h-full bg-secondary z-[100] lg:hidden rounded-xl shadow-all'
                            >
                                <SideMenu
                                    closeMenu={() => setMenuOpen(false)}
                                    deactivate={() => {
                                        setMenuOpen(false)
                                        setIsDeactivate(true)
                                    }}
                                    logout={() => {
                                        setMenuOpen(false)
                                        setIsLogout(true)
                                    }}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className='flex  lg:flex-row mx-4 lg:gap-14 md:mx-6 lg:mx-14 '>
                        <div className='hidden lg:block h-full rounded-xl relative lg:w-72 mt-24 lg:-mt-24 mb-20'>
                            <SideMenu
                                deactivate={() => setIsDeactivate(true)}
                                logout={() => {
                                    setIsLogout(true)
                                }}
                            />
                        </div>
                        <Outlet />
                    </div>
                </div>
            )}

            <DeactivateModel
                isOpen={isDeactivate}
                onClose={() => {
                    setIsDeactivate(false);
                }}
            />

            <LogoutModel
                isOpen={isLogout}
                onClose={() => {
                    setIsLogout(false);
                }}
            />

            {expiryPopup && (
                <PlanExpiryModal
                    daysRemaining={expiryPopup.daysRemaining}
                    endDate={expiryPopup.endDate}
                    onClose={() => {
                        markShownToday();
                        setExpiryPopup(null);
                    }}
                />
            )}
        </>
    );
}

export default HomeLayout;
